import { z } from 'zod';

const boundedVersion = z.string().min(1).max(128);
const boundedIdentifier = z.string().min(1).max(128);
const unixMilliseconds = z.number().int().nonnegative().safe();

export const macroHostComponentVersionsSchema = z.object({
  installedRegistry: boundedVersion.nullable(),
  nodeService: boundedVersion.nullable(),
  hostElectron: boundedVersion.nullable(),
  playjector: boundedVersion.nullable(),
}).strict();
export type MacroHostComponentVersions = z.infer<typeof macroHostComponentVersionsSchema>;

export const macroCompatibilityRequirementSchema = z.object({
  branch: boundedIdentifier,
  playjectorVersion: boundedVersion,
  macroFormat: z.literal('playcast.macro.compiled'),
  macroFormatVersion: z.number().int().positive().safe(),
  protocolVersion: z.number().int().positive().safe(),
}).strict();
export type MacroCompatibilityRequirement = z.infer<typeof macroCompatibilityRequirementSchema>;

export const macroAdmissionBlockerSchema = z.enum([
  'asset_origin_unreachable',
  'host_disconnected',
  'host_component_missing',
  'host_versions_diverged',
  'host_branch_mismatch',
  'playjector_incompatible',
  'campaign_unassigned',
  'package_not_provisioned',
  'calibration_missing',
  'calibration_failed',
  'source_revision_stale',
  'validation_receipt_stale',
]);
export type MacroAdmissionBlocker = z.infer<typeof macroAdmissionBlockerSchema>;

const blockersSchema = z.array(macroAdmissionBlockerSchema).max(32)
  .refine(blockers => new Set(blockers).size === blockers.length, 'Admission blockers must be unique');

export const macroHostReadinessSchema = z.object({
  campaignId: boundedIdentifier,
  machineId: boundedIdentifier,
  observedAtUnixMs: unixMilliseconds,
  components: macroHostComponentVersionsSchema,
  required: macroCompatibilityRequirementSchema,
  hostConnected: z.boolean(),
  campaignAssigned: z.boolean(),
  packageProvisioned: z.boolean(),
  ready: z.boolean(),
  blockers: blockersSchema,
}).strict().superRefine((value, context) => {
  if (value.ready !== (value.blockers.length === 0)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['ready'], message: 'Ready iff blockers are empty' });
  }
});
export type MacroHostReadiness = z.infer<typeof macroHostReadinessSchema>;

export const macroOperationAdmissionSchema = z.object({
  operation: z.enum(['calibrate', 'compile', 'lock', 'execute']),
  admitted: z.boolean(),
  blockers: blockersSchema,
  readiness: macroHostReadinessSchema.nullable(),
}).strict().superRefine((value, context) => {
  if (value.admitted !== (value.blockers.length === 0)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['admitted'], message: 'Admitted iff blockers are empty' });
  }
});
export type MacroOperationAdmission = z.infer<typeof macroOperationAdmissionSchema>;

export const macroAnalysisTerminalDiagnosticSchema = z.object({
  jobId: z.string().uuid(),
  status: z.enum(['completed', 'failed', 'cancelled', 'superseded']),
  stage: z.enum(['schedule', 'grant', 'read', 'compare', 'validate', 'attach']),
  errorCode: z.string().min(1).max(128).nullable(),
  referenceIds: z.array(boundedIdentifier).max(4_096)
    .refine(ids => new Set(ids).size === ids.length, 'Reference ids must be unique'),
  summary: z.string().min(1).max(1_024),
  completedAtUnixMs: unixMilliseconds,
}).strict().superRefine((value, context) => {
  if (value.status === 'failed' && value.errorCode === null) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['errorCode'], message: 'Failed analysis requires an error code' });
  }
  if (value.status === 'completed' && value.errorCode !== null) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['errorCode'], message: 'Completed analysis cannot carry an error code' });
  }
});
export type MacroAnalysisTerminalDiagnostic = z.infer<typeof macroAnalysisTerminalDiagnosticSchema>;

export const getMacroReadinessResponseSchema = z.object({
  admission: macroOperationAdmissionSchema,
  analysis: macroAnalysisTerminalDiagnosticSchema.nullable(),
}).strict();
export type GetMacroReadinessResponse = z.infer<typeof getMacroReadinessResponseSchema>;
